import { ConfigData } from 'serviceWorker/db/config';
import { ChartDataEntity } from 'serviceWorker/db/analytics';
import { HashMap } from 'shared/types/commonView';
import { IssueEntity } from 'serviceWorker/repository/issue/entity';
import type { ChartAdditionalParams, ChartSeries } from 'shared/domain/analytics/chartModel';
import { ensureAuthenticatedConfig } from '../config';
import {
  CalculationFunction,
  IssueChart,
  CostsUnion,
  EstimatedCost,
  EstimatedSingleCost,
  FinalCost,
  FinalSingleCost,
  FilterValues,
  Finances,
  InspectionChart,
} from 'shared/types/analytics';

const CHART_CACHE_TIME = 1000 * 60 * 60 * 3; // 3h

/** Config of the selected project; rejects without one, so a chart request answers instead of waiting. */
export async function getAnalyticsConfig(): Promise<ConfigData & { projectId: string; timezone: string }> {
  const configData = await ensureAuthenticatedConfig();
  if (!configData?.projectId) {
    throw new Error('Cannot calculate analytics without a selected project.');
  }
  return {
    ...configData,
    projectId: configData.projectId,
    // Configs stored before PT-7138 have no timezone: the worker's own, as calculations used then.
    timezone: configData.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

/** The processes the user may see, which issue charts by field value need; throws without them. */
export function requireVisibleProcesses(additionalParams: ChartAdditionalParams | undefined): string[] {
  if (!additionalParams?.visibleProcesses) {
    throw new Error('Missing visibleProcesses param');
  }
  return additionalParams.visibleProcesses;
}

/**
 * The issues of the processes where the chart's field is shown: an issue of
 * another process has no such field, so it is neither counted nor taken for
 * one without a value (PT-6804: a process filter of such a process shows no data).
 */
export function keepIssuesOfVisibleProcesses(
  issues: IssueEntity[],
  additionalParams: ChartAdditionalParams | undefined
): IssueEntity[] {
  const visibleProcesses = requireVisibleProcesses(additionalParams);
  return issues.filter((issue) => visibleProcesses.includes(issue.process));
}

export function createChartDataEntity(
  series: ChartSeries,
  filters: FilterValues,
  name: IssueChart | InspectionChart,
  projectId: string,
  locale: string,
  additionalParams: ChartAdditionalParams | undefined,
  updatedAt: number
): ChartDataEntity {
  return {
    _id: `${projectId}_${name}`,
    series,
    filters,
    name,
    projectId,
    locale,
    additionalParams,
    updatedAt,
  };
}

function areObjectsEqual(curr: unknown, prev: unknown): boolean {
  const result = JSON.stringify(curr) === JSON.stringify(prev);
  return result;
}

export function calculateFinances(issues: IssueEntity[]): Finances {
  const finances = issues.reduce<Finances>(
    (result: Finances, issue: IssueEntity): Finances => {
      const newCosts: Finances = {
        estimatedCosts: calculateCost(
          issue.extendedData.estimatedCost,
          updateCost
        ),
        finalCosts: calculateCost(
          issue.extendedData.finalCost,
          updateCostObject
        ),
        expectedFines: calculateFine(
          issue.primaryData.subcontractors || [],
          issue.extendedData.expectedFine,
          updateFine
        ),
        imposedFines: calculateFine(
          issue.primaryData.subcontractors || [],
          issue.extendedData.imposedFine,
          updateFine
        ),
      };

      updateAllCosts(result, newCosts);
      return result;
    },
    {
      estimatedCosts: {},
      finalCosts: {},
      expectedFines: {},
      imposedFines: {},
    }
  );
  return finances;
}

function calculateCost<T>(
  issueCosts: CostsUnion[] | undefined,
  calculationFunction: CalculationFunction<T>
): HashMap<T> {
  const resultCosts: HashMap<T> = {};
  if (Array.isArray(issueCosts)) {
    issueCosts.forEach((cost) => {
      if (!cost.coveredBy) {
        return;
      }
      resultCosts[cost.coveredBy] = calculationFunction(
        resultCosts[cost.coveredBy],
        cost
      );
    });
  }

  return resultCosts;
}

function updateAllCosts(currentCosts: Finances, newCosts: Finances): void {
  updateCosts(
    'estimatedCosts',
    currentCosts,
    newCosts.estimatedCosts,
    updateCost
  );
  updateCosts(
    'finalCosts',
    currentCosts,
    newCosts.finalCosts,
    updateCostObject
  );
  updateCosts(
    'expectedFines',
    currentCosts,
    newCosts.expectedFines,
    updateFine
  );
  updateCosts(
    'imposedFines',
    currentCosts,
    newCosts.imposedFines,
    updateFine
  );
}

function updateCosts(
  costKey: keyof Finances,
  currentCosts: Finances,
  newCosts: Finances[keyof Finances],
  // im unable to type it properly
  calculationFunction: CalculationFunction<any>
): void {
  Object.entries(newCosts).forEach((entry: [string, any]) => {
    const [company, cost] = entry;
    currentCosts[costKey][company] = calculationFunction(
      currentCosts[costKey][company],
      cost
    );
  });
}

function updateCostObject(
  maybeCost: FinalCost | undefined,
  cost: FinalSingleCost
): FinalCost {
  if (!maybeCost) {
    return {
      outstanding: parseFloat(cost.outstanding),
      settled: parseFloat(cost.settled),
    };
  } else {
    return {
      outstanding: maybeCost.outstanding + parseFloat(cost.outstanding),
      settled: maybeCost.settled + parseFloat(cost.settled),
    };
  }
}

function updateCost(
  maybeCost: EstimatedCost | undefined,
  costObject: EstimatedSingleCost
): EstimatedCost {
  if (!maybeCost) {
    return {
      cost: parseFloat(costObject.cost),
    };
  } else {
    return {
      cost: maybeCost.cost + parseFloat(costObject.cost),
    };
  }
}

function calculateFine(
  subcontractors: string[],
  issueFine: number | undefined,
  calculationFunction: CalculationFunction<number>
): HashMap<number> {
  const resultCosts: HashMap<number> = {};
  if (Array.isArray(subcontractors) && issueFine) {
    subcontractors.forEach((subcontractor) => {
      resultCosts[subcontractor] = calculationFunction(
        resultCosts[subcontractor],
        issueFine
      );
    });
  }

  return resultCosts;
}

function updateFine(maybeFine: number | undefined, fine: number): number {
  if (maybeFine === undefined) {
    return fine;
  } else {
    return maybeFine + fine;
  }
}

export function canUseCache(cachedAt: number): boolean {
  return Date.now() - cachedAt < CHART_CACHE_TIME;
}

export function canReuseLastSavedChart(
  recievedChart: ChartDataEntity | undefined,
  forceUpdate: boolean | undefined,
  locale: string,
  filtersFromClient: FilterValues,
  additionalParams: ChartAdditionalParams | undefined
): recievedChart is ChartDataEntity {
  return Boolean(
    recievedChart &&
      !forceUpdate &&
      locale === recievedChart.locale &&
      canUseCache(recievedChart.updatedAt) &&
      equalFilters(filtersFromClient, recievedChart.filters) &&
      equalParams(additionalParams, recievedChart.additionalParams)
  );
}

function equalFilters(
  filtersRecievedFromClient: FilterValues | undefined,
  currentSeriesFilters: FilterValues | undefined
): boolean {
  if (!filtersRecievedFromClient) {
    return Boolean(currentSeriesFilters);
  }
  if (!currentSeriesFilters) {
    return false;
  }

  return areObjectsEqual(filtersRecievedFromClient, currentSeriesFilters);
}

function equalParams(
  paramsRecievedFromClient: ChartAdditionalParams | undefined,
  currentSeriesParams: ChartAdditionalParams | undefined
): boolean {
  if (!paramsRecievedFromClient && !currentSeriesParams) {
    return true;
  }

  return areObjectsEqual(paramsRecievedFromClient, currentSeriesParams);
}
